import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Activity, ArrowLeft, RefreshCw, Users, Store, DollarSign,
  TrendingUp, CheckCircle, Clock, XCircle, AlertTriangle, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../utils/apiConfig';

// Tab definitions
const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'financial', label: 'Financial Analytics', icon: DollarSign, route: '/admin/analytics/financial' },
  { id: 'transactions', label: 'Transactions', icon: BarChart3, route: '/admin/transactions' },
  { id: 'merchants', label: 'Merchants', icon: Store, route: '/admin/users?role_filter=merchant' },
  { id: 'customers', label: 'Customers', icon: Users, route: '/admin/users?role_filter=sender' },
  { id: 'authorizations', label: 'Authorizations', icon: CheckCircle, route: '/admin/authorizations' },
];

export default function AdminAnalyticsView() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();
  const location = useLocation();

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchAnalytics = useCallback(async () => {
    const token = getToken();
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/master`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 403) {
          navigate('/admin');
          return;
        }
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [navigate, isAdmin]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleTabClick = (tab) => {
    if (tab.route) {
      navigate(tab.route);
    } else {
      setActiveTab(tab.id);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <div className={`flex items-center gap-2 ${color} mb-2`}>
        <Icon className="w-5 h-5" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {subtext && <p className="text-sm text-slate-500 mt-1">{subtext}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Analytics</h1>
              <p className="text-sm text-slate-400">Platform-wide metrics and insights</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {analytics?.pilot_mode && (
              <span className="px-3 py-1 bg-amber-900/50 text-amber-300 text-xs rounded-full border border-amber-700">
                PILOT MODE
              </span>
            )}
            <Button
              onClick={fetchAnalytics}
              variant="outline"
              className="border-slate-600 text-slate-300"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id && !tab.route;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive 
                      ? 'bg-purple-600 text-white' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-10 h-10 text-purple-400 animate-spin" />
          </div>
        ) : analytics ? (
          <div className="space-y-8">
            {/* Users Section */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Users
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={Users}
                  label="Total Users"
                  value={analytics.users?.total || 0}
                  color="text-blue-400"
                />
                <StatCard
                  icon={Users}
                  label="Senders"
                  value={analytics.users?.senders || 0}
                  color="text-blue-400"
                />
                <StatCard
                  icon={Store}
                  label="Merchants"
                  value={analytics.users?.merchants || 0}
                  color="text-emerald-400"
                />
              </div>
            </div>

            {/* Transactions Section */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Transactions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={Activity}
                  label="Total Transactions"
                  value={analytics.transactions?.total || 0}
                  color="text-purple-400"
                />
                <StatCard
                  icon={CheckCircle}
                  label="Completed"
                  value={analytics.transactions?.completed || 0}
                  color="text-emerald-400"
                />
                <StatCard
                  icon={DollarSign}
                  label="Total Volume"
                  value={`$${(analytics.transactions?.total_volume_usd || 0).toLocaleString()}`}
                  color="text-amber-400"
                />
              </div>
            </div>

            {/* Authorizations Section */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-400" />
                Purchase Authorizations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                  icon={Activity}
                  label="Total"
                  value={analytics.authorizations?.total || 0}
                  color="text-slate-400"
                />
                <StatCard
                  icon={Clock}
                  label="Pending"
                  value={analytics.authorizations?.pending || 0}
                  color="text-amber-400"
                />
                <StatCard
                  icon={CheckCircle}
                  label="Active"
                  value={analytics.authorizations?.active || 0}
                  color="text-blue-400"
                />
                <StatCard
                  icon={TrendingUp}
                  label="Partially Redeemed"
                  value={analytics.authorizations?.partially_redeemed || 0}
                  color="text-purple-400"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <StatCard
                  icon={CheckCircle}
                  label="Completed"
                  value={analytics.authorizations?.completed || 0}
                  color="text-emerald-400"
                />
                <StatCard
                  icon={XCircle}
                  label="Cancelled"
                  value={analytics.authorizations?.cancelled || 0}
                  color="text-red-400"
                />
                <StatCard
                  icon={AlertTriangle}
                  label="Expired"
                  value={analytics.authorizations?.expired || 0}
                  color="text-orange-400"
                />
                <StatCard
                  icon={DollarSign}
                  label="Outstanding Balance"
                  value={`$${(analytics.authorizations?.total_outstanding_balance_usd || 0).toLocaleString()}`}
                  subtext="Pending redemption"
                  color="text-amber-400"
                />
              </div>
            </div>

            {/* Financial Summary */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Financial Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                  icon={TrendingUp}
                  label="Total Authorized"
                  value={`$${(analytics.authorizations?.total_authorized_volume_usd || 0).toLocaleString()}`}
                  color="text-blue-400"
                />
                <StatCard
                  icon={CheckCircle}
                  label="Total Redeemed"
                  value={`$${(analytics.authorizations?.total_redeemed_volume_usd || 0).toLocaleString()}`}
                  color="text-emerald-400"
                />
                <StatCard
                  icon={Clock}
                  label="Outstanding"
                  value={`$${(analytics.authorizations?.total_outstanding_balance_usd || 0).toLocaleString()}`}
                  color="text-amber-400"
                />
                <StatCard
                  icon={XCircle}
                  label="Cancelled/Released"
                  value={`$${(analytics.authorizations?.total_cancelled_released_usd || 0).toLocaleString()}`}
                  color="text-red-400"
                />
              </div>
            </div>

            {/* Quick Link to Financial Analytics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border border-purple-700/50 rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Financial Analytics</h3>
                  <p className="text-slate-400 text-sm">
                    Deep dive into revenue, fees, transaction costs, and unit economics
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/admin/analytics/financial')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  View Financial Analytics
                </Button>
              </div>
            </motion.div>
          </div>
        ) : (
          <div className="text-center py-20 text-slate-400">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No analytics data available</p>
          </div>
        )}
      </main>
    </div>
  );
}
